import type { IStore } from "~/state/store";
import type Wasm from "../wasm";
// eslint-disable-next-line no-duplicate-imports
import type { TWasmMapHover } from "../wasm";
import type WasmStoreMapping from "../mapping";
import type CursorManager from "~/util/cursorManager";
import { updateMarker } from "~/state/map-content/state";
import { setHoverSubscription } from "~/state/share-position/state";
import { getPopupPoiOffset, hidePopup, isPopupConnected, isRouteActive, popupAlwaysOnMap, popupImport, popupMM, popupPOI, popupSearch, popupSharePosition, setRouteSummaryPopup, showPopup, unsetRouteSummaryPopup, updatePopupPosition, useCursor } from "./util";
import { setMMPopupContent } from "./popupcontent";
import { openDIM } from "~/util/userweb";
import { CONFIG } from "~/config";

export function onHover(data: TWasmMapHover, store: IStore, cursorManager: CursorManager, mapping: WasmStoreMapping, wasm: Wasm) {
	//console.log("hover", data.poi ? `${data.poi.source}-${data.poi.id}` : data.wasmId, data.type, data.actionUserData);
	// vlastni body - hover nad markerem
	if (Mapy.getComponent("userMarks").isActive()) {
		if (data.type !== "move" && data.wasmId) {
			const { wasmMarker: userMarksMarker } = mapping.getWasmToStoreData(data.wasmId);

			if (userMarksMarker?.type === "user-marks") {
				Mapy.getComponent("userMarks").getMain()?.sectionHighlight(data.type === "enter", userMarksMarker.customData?.order - 1);
				useCursor("grab", data, cursorManager);
			}
		}
	} else if (Mapy.getComponent("distanceMeter").isActive() && data.type !== "move" && data.wasmId) {
		const { wasmMarker: distanceMeterMarker, wasmGeometry: distanceMeterGeometry } = mapping.getWasmToStoreData(data.wasmId);

		if (distanceMeterMarker?.type === "measure-points" || distanceMeterMarker?.type === "measure-start") {
			Mapy.getComponent("distanceMeter").getMain()?.sectionHighlight(data.type === "enter", Math.max(distanceMeterMarker.customData?.index - 1, 0), true);
			useCursor("grab", data, cursorManager);
		}

		if (distanceMeterGeometry?.type === "measure-line") {
			Mapy.getComponent("distanceMeter").getMain()?.sectionHighlight(data.type === "enter", distanceMeterGeometry.customData?.index, true);
			useCursor("url(/img/distancemeter/add.png) 3 3, crosshair", data, cursorManager);
		}
	// nejaky jiny marker nebo geometrie, co jsme si sami dali do mapy
	} else if (data.wasmId) {
		const { wasmMarker, wasmGeometry } = mapping.getWasmToStoreData(data.wasmId);

		if (wasmMarker && data.type !== "move") {
			// neco, co otevira POI nebo trvale nad mapou a veci z MM nebo aktivita
			if (wasmMarker.customData?.poi
				|| wasmMarker?.type === "route-popup-info"
				|| wasmMarker.customData?.dim
				|| wasmMarker.customData?.component === "activities"
				|| wasmMarker.customData?.subscriptionId
				|| wasmMarker.type === "photo"
				|| wasmMarker.type === "webcam"
			) {
				useCursor("pointer", data, cursorManager);
				// vysledky hledani
				if (wasmMarker.customData.component === "search" && wasmMarker.customData.poi) {
					Mapy.getComponent("search").hoverItem({
						source: wasmMarker.customData.poi.source,
						id: wasmMarker.customData.poi.id,
						state: data.type,
					});

					if (data.type === "enter") {
						popupSearch(wasmMarker, wasm);
					}
				// vec z MM, ale ne trvale nad mapou
				} else if (Mapy.getComponent("places").isActive && wasmMarker.customData.dim && wasmMarker.type !== "always-on-map") {
					Mapy.getComponent("places").highlightItem(data.type === "enter" ? wasmMarker.customData.dim : "");

					if (data.type === "enter") {
						popupMM(wasmMarker, wasm);
					}
				// vzdy nad mapou
				} else if (wasmMarker.customData.dim && wasmMarker.type === "always-on-map" && data.type === "enter") {
					popupAlwaysOnMap(wasmMarker, wasm);
				// aktivita v MM
				} else if (wasmMarker.customData?.component === "activities") {
					Mapy.getComponent("activities").highlight(data.type === "enter" ? wasmMarker.customData.customId : "");

					if (data.type === "enter") {
						popupAlwaysOnMap(wasmMarker, wasm);
					}
				// sdilena poloha
				} else if (wasmMarker.customData?.subscriptionId) {
					if (data.type === "enter") {
						popupSharePosition(wasmMarker, wasm);
						store.dispatch(setHoverSubscription(wasmMarker.customData.subscriptionId));
					} else {
						store.dispatch(setHoverSubscription(""));
					}
				}
			} else if (isRouteActive() && (
				wasmMarker?.type === "route-popup-closure"
				|| wasmMarker?.type === "route-popup-pubt-section"
				|| wasmMarker?.type === "route-popup-pubt-summary"
				|| wasmMarker?.type === "route-popup-weather"
			)) {
				useCursor("pointer", data, cursorManager);
			// neco tahaciho - obvykle nic nedela, jen meni kurzor
			} else if (wasmMarker?.draggable) {
				useCursor("grab", data, cursorManager);
				// routovaci marker
				if (wasmMarker.customData?.route) {
					Mapy.CML.state = {
						route: {
							hoverInd: data.type === "enter" ? wasmMarker.customData.order - 1 : -1,
						},
					};
				}
			}
		}

		// tohle je pojistka pro pripad, kdy dostanu leave na necem, co ale v mape a tedy ani ve store uz neni (stava se pri dnd v route, kdy po presunuti markeru marker z mapy smazeme a preroutujeme, ale mys se nepohne, dokud marker neni z mapy pryc)
		if (data.type === "leave") {
			hidePopup();
			useCursor("", data, cursorManager);
		}

		if (wasmGeometry) {
			const { markers } = store.getState().mapContent;

			// geometrie routovace
			if (isRouteActive() && wasmGeometry.customData?.route?.routeIndex !== undefined) {
				// aktualni zvolena route reaguje na hover
				if (wasmGeometry.customData?.route && (wasmGeometry.customData.route.routeIndex === Mapy.CML.state.route.index)) {
					// pohyb po geometrii aktualni route
					if (data.type === "move") {
						setRouteSummaryPopup([Mapy.CML.state.route.responseData[Mapy.CML.state.route.index].geometry], 0, data.contactX, data.contactY, wasm);
					// enter/leave na geometrii aktualni route
					} else {
						if (data.type === "enter") {
							setRouteSummaryPopup([Mapy.CML.state.route.responseData[Mapy.CML.state.route.index].geometry], 0, data.contactX, data.contactY, wasm);
							document.body.addEventListener("pointerdown", unsetRouteSummaryPopup);
						} else {
							unsetRouteSummaryPopup();
							document.body.removeEventListener("pointerdown", unsetRouteSummaryPopup);
						}

						markers.forEach(marker => {
							if (marker.customData?.route) {
								store.dispatch(updateMarker({
									id: marker.id,
									data: data.type === "enter"
										? {
											lastChange: marker.lastChange + 1,
											interactionType: "none",
											visual: "opacity",
											draggable: false,
										}
										: {
											lastChange: marker.lastChange + 1,
											interactionType: "click-hover",
											visual: "standard",
											draggable: true,
										},
								}));
							}
						});
					}
				// prepinani alternativy v routovaci
				} else {
					useCursor("pointer", data, cursorManager);
				}
			// geometrie neceho z MM
			} else if (Mapy.getComponent("places").isActive && wasmGeometry.customData?.dim) {
				useCursor("pointer", data, cursorManager);
				data.type !== "move" && Mapy.getComponent("places").highlightItem(data.type === "enter" ? wasmGeometry.customData.dim : "");
				if (data.type === "enter") {
					const clientX = data.contactX;
					const clientY = data.contactY;

					showPopup({
						clientX,
						clientY,
						contentBuilder: () => setMMPopupContent(wasmGeometry.customData.mmItem, false, (_event, item) => {
							openDIM(item.id, { inFrame: CONFIG.inFrame }).then(() => {
								if (!CONFIG.inFrame) {
									Mapy.getComponent("history").save();
								}
							}, () => {});
						}),
						mode: "geometry",
					});
				} else if (data.type === "move") {
					updatePopupPosition(data.contactX, data.contactY);
				}
			// pouzijeme i pro detail cesty
			} else if (wasmGeometry.customData?.component === "activity" || wasmGeometry.customData?.component === "path") {
				if (data.type === "move") {
					setRouteSummaryPopup(wasmGeometry.strPoints, data.actionUserData?.multilineIndex || 0, data.contactX, data.contactY, wasm);
				// enter/leave na geometrii detailu aktivity
				} else {
					if (data.type === "enter") {
						setRouteSummaryPopup(wasmGeometry.strPoints, data.actionUserData?.multilineIndex || 0, data.contactX, data.contactY, wasm);
						document.body.addEventListener("pointerdown", unsetRouteSummaryPopup);
					} else {
						unsetRouteSummaryPopup();
						document.body.removeEventListener("pointerdown", unsetRouteSummaryPopup);
					}

					markers.forEach(marker => {
						if (marker.customData?.component === "activity" || marker.customData?.component === "path") {
							store.dispatch(updateMarker({
								id: marker.id,
								data: data.type === "enter"
									? {
										lastChange: marker.lastChange + 1,
										visual: "opacity",
									}
									: {
										lastChange: marker.lastChange + 1,
										visual: "standard",
									},
							}));
						}
					});
				}
			}
		}

		// marker nebo geometrie z importu GPX
		if (data.type !== "move" && (wasmMarker?.customData?.component === "import" || wasmGeometry?.customData?.component === "import")) {
			const wasmEntity = wasmMarker || wasmGeometry;
			const customId: string = wasmEntity.customData?.customId as string;

			useCursor("pointer", data, cursorManager);
			Mapy.getComponent("gpx").itemHover(customId, data.type !== "leave");

			if (data.type === "enter") {
				popupImport(wasmMarker, wasmGeometry, wasm, {
					x: data.contactX,
					y: data.contactY,
				});
			}
		}
	// hover nad panorama dostupnosti
	} else if (data.rasterLayer === "panoLayer") {
		useCursor("pointer", data, cursorManager);
	// hover nad necim, co otevira POI
	} else if (data.poi) {
		if (data.type === "move") {
			if (data.poi.geomType === "Polygon" && !isPopupConnected()) {
				const pos = getPopupPoiOffset(data.clientX, data.clientY);

				updatePopupPosition(pos.x, pos.y);
			}
		} else {
			let usePointer = true;

			if (data.poi.source === "plot") {
				usePointer = Mapy.getComponent("pois").isIdCadastral(data.poi.id);
			}

			usePointer && useCursor("pointer", data, cursorManager);

			if (data.type === "enter" && data.poi.source !== "plot") {
				popupPOI(data, wasm);
			} else {
				hidePopup();
			}
		}
	}
}
